/**
 * PULL workflow on-demand activation (OpCon parity — ACME-1358 / Story 1.1).
 */

import Cookies from "js-cookie";
import { Apis } from "./api-constants";
import { decryptValue, isSessionExpired } from "./app-utils";
import { fixedEncodeURIComponent, onSessionTimedOut } from "./jha-fetch";
import { getRuntimeConfig } from "./runtime-config";
import { getErrorMessage } from "./getErrorMessage";

/** UI uses a shorter wait than OpCon (300s) to stay under typical edge proxy timeouts (e.g. HTTP 524). */
export const UI_ACTIVATION_MAX_WAIT_SECONDS = 60;

const ACTIVATION_FETCH_TIMEOUT_BUFFER_MS = 15_000;

function activationFetchTimeoutMs(maxWaitSeconds?: number): number {
	const seconds = maxWaitSeconds ?? 300;
	return seconds * 1000 + ACTIVATION_FETCH_TIMEOUT_BUFFER_MS;
}

export const WORKFLOW_ACTIVATION_ERROR_CODES = {
	PUSH_NOT_ALLOWED: "63.03.0082",
	ACTIVATION_DISABLED: "63.03.0083",
	INACTIVE_WORKFLOW: "63.03.0084",
} as const;

export interface WorkflowActivationRequest {
	maxWait?: number;
}

export interface WorkflowActivationResponse {
	status: "completed" | "in_progress";
	traceId: string;
	maxWaitApplied: number;
	reasonCode?: "already_running";
	outcome?: "success" | "fail";
	processedFiles?: string[];
	messageStatus?: unknown;
}

export interface WorkflowActivationErrorDetail {
	code?: string;
	description?: string;
}

export interface WorkflowActivationResult {
	httpStatus: number;
	ok: boolean;
	body: WorkflowActivationResponse | Record<string, unknown> | null;
}

export type WorkflowActivationToastRole = "success" | "danger";

export interface WorkflowActivationUserMessage {
	message: string;
	role: WorkflowActivationToastRole;
	/** Navigate to audit details for this trace when set. */
	auditTraceId?: string;
	/** Pre-filter audit list by workflow name when set. */
	auditWorkflowName?: string;
}

function getAuthToken(): string | undefined {
	const isLocalhost =
		typeof window !== "undefined" &&
		(window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

	const fromCookie = Cookies.get("token");
	const envToken = isLocalhost ? (import.meta.env.VITE_JHA_SSO_TOKEN as string | undefined) : undefined;
	if (isLocalhost && envToken) return envToken;
	return fromCookie;
}

function getUserId(): string | undefined {
	const fromCookie = Cookies.get("userId");
	if (fromCookie) return decryptValue(fromCookie ?? "") || undefined;
	if (
		typeof window !== "undefined" &&
		(window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
	) {
		const envUserId = import.meta.env.VITE_DEV_USER_ID as string | undefined;
		if (envUserId) return envUserId;
	}
	return undefined;
}

function resolveApiUrl(url: string): string {
	const rc = getRuntimeConfig();
	const instId = rc.institutionUniversalId;
	const isLocalhostDev =
		typeof window !== "undefined" &&
		(window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") &&
		import.meta.env.VITE_USE_DEV_REFERENCE_DATA === "true";
	const devApiBase = isLocalhostDev ? (import.meta.env.VITE_DEV_REFERENCE_DATA_URL as string | undefined) : undefined;
	const localApiBase = isLocalhostDev ? rc.apiBaseUrl : undefined;

	if (!isLocalhostDev || !devApiBase || !localApiBase || !url.startsWith(localApiBase)) {
		return url;
	}

	const localPaths = String(import.meta.env.VITE_LOCAL_API_PATHS ?? "")
		.split(",")
		.map((s) => s.trim())
		.filter(Boolean);

	for (const entry of localPaths) {
		const colonIdx = entry.lastIndexOf(":");
		const segment = entry.slice(0, colonIdx);
		const port = entry.slice(colonIdx + 1);
		if (colonIdx > 0 && /^\d+$/.test(port) &&
			(url.includes(`/${instId}/${segment}/`) || url.includes(`/${instId}/reference/${segment}/`))
		) {
			try {
				const parsed = new URL(localApiBase);
				parsed.port = port;
				return url.replace(localApiBase, parsed.origin);
			} catch {
				return url;
			}
		}
	}

	return url.replace(localApiBase, devApiBase);
}

function extractErrorDetails(body: Record<string, unknown> | null): WorkflowActivationErrorDetail[] {
	if (!body) return [];
	const top = body.details;
	if (Array.isArray(top)) return top as WorkflowActivationErrorDetail[];
	const ms = body.messageStatus as Record<string, unknown> | undefined;
	if (ms && Array.isArray(ms.details)) return ms.details as WorkflowActivationErrorDetail[];
	return [];
}

function hasErrorCode(details: WorkflowActivationErrorDetail[], code: string): boolean {
	return details.some((d) => d.code === code);
}

/**
 * POST workflow-activate with HTTP status preserved and extended timeout for long-poll.
 */
export async function activateWorkflow(
	workflowId: string,
	request?: WorkflowActivationRequest,
): Promise<WorkflowActivationResult> {
	if (isSessionExpired()) {
		onSessionTimedOut();
		return { httpStatus: 0, ok: false, body: null };
	}

	const url =
		resolveApiUrl(Apis.WORKFLOW_ACTIVATE) + fixedEncodeURIComponent(workflowId);
	const token = getAuthToken();
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), activationFetchTimeoutMs(request?.maxWait));

	try {
		const response = await fetch(url, {
			method: "POST",
			mode: "cors",
			cache: "no-cache",
			credentials: "same-origin",
			headers: {
				Authorization: "Bearer " + token,
				"Content-Type": "application/json",
				"X-Frame-Options": "sameorigin",
				"Content-Security-Policy": "self",
				userId: getUserId() ?? "",
			},
			body: JSON.stringify(request ?? {}),
			signal: controller.signal,
		});

		let body: WorkflowActivationResponse | Record<string, unknown> | null = null;
		const text = await response.text();
		if (text) {
			try {
				body = JSON.parse(text) as WorkflowActivationResponse | Record<string, unknown>;
			} catch {
				body = null;
			}
		}

		return { httpStatus: response.status, ok: response.ok, body };
	} catch (e) {
		if (e instanceof DOMException && e.name === "AbortError") {
			return {
				httpStatus: 0,
				ok: false,
				body: {
					details: [
						{
							description:
								"The activation request timed out. The workflow may still be running — check Audit Reporting for status.",
						},
					],
				},
			};
		}
		return { httpStatus: 0, ok: false, body: null };
	} finally {
		clearTimeout(timeoutId);
	}
}

export function buildWorkflowActivationUserMessage(
	result: WorkflowActivationResult,
	workflowName?: string,
): WorkflowActivationUserMessage {
	const wfName = (workflowName ?? "").trim();
	const body = (result.body ?? {}) as WorkflowActivationResponse & Record<string, unknown>;
	const details = extractErrorDetails(result.body as Record<string, unknown> | null);

	if (result.httpStatus === 403 || hasErrorCode(details, WORKFLOW_ACTIVATION_ERROR_CODES.ACTIVATION_DISABLED)) {
		return {
			message: "Workflow activation is temporarily disabled.",
			role: "danger",
		};
	}

	if (hasErrorCode(details, WORKFLOW_ACTIVATION_ERROR_CODES.INACTIVE_WORKFLOW)) {
		return {
			message:
				getErrorMessage(details) ||
				"Only active workflows can be activated. Set the workflow status to active first.",
			role: "danger",
		};
	}

	if (hasErrorCode(details, WORKFLOW_ACTIVATION_ERROR_CODES.PUSH_NOT_ALLOWED)) {
		return {
			message: getErrorMessage(details) || "Only PULL workflows can be activated through this action.",
			role: "danger",
		};
	}

	if (result.httpStatus === 524 || result.httpStatus === 504) {
		const wfPart = wfName ? ` for "${wfName}"` : "";
		return {
			message: `The connection timed out while waiting for the workflow run${wfPart}. The run may still be in progress — check Audit Reporting for status.`,
			role: "success",
			auditWorkflowName: wfName || undefined,
		};
	}

	if (result.httpStatus === 0 && !result.body) {
		const wfPart = wfName ? ` for "${wfName}"` : "";
		return {
			message: `Could not reach the server to start the workflow run${wfPart}. The run may still have started — check Audit Reporting, or try again.`,
			role: "danger",
			auditWorkflowName: wfName || undefined,
		};
	}

	if (!result.ok || result.httpStatus >= 400) {
		if (details.length > 0) {
			return { message: getErrorMessage(details), role: "danger" };
		}
		return { message: "Something went wrong, please try again.", role: "danger" };
	}

	const activation = body as WorkflowActivationResponse;
	if (activation.status === "completed") {
		const fileCount = activation.processedFiles?.length ?? 0;
		const traceSuffix = activation.traceId ? ` Trace ID: ${activation.traceId}.` : "";
		if (activation.outcome === "fail") {
			const filesPart =
				fileCount > 0 ? ` ${fileCount} file(s) were processed before failure.` : " No files were processed.";
			return {
				message: `Workflow run completed with errors.${filesPart}${traceSuffix}`,
				role: "danger",
				auditTraceId: activation.traceId,
				auditWorkflowName: wfName || undefined,
			};
		}
		const filesPart =
			fileCount > 0 ? ` ${fileCount} file(s) processed.` : fileCount === 0 ? " No files to process." : "";
		return {
			message: `Workflow run completed successfully.${filesPart}${traceSuffix}`,
			role: "success",
			auditTraceId: activation.traceId,
			auditWorkflowName: wfName || undefined,
		};
	}

	if (activation.status === "in_progress") {
		if (activation.reasonCode === "already_running") {
			return {
				message: `A run is already in progress for this workflow. Trace ID: ${activation.traceId}.`,
				role: "success",
				auditTraceId: activation.traceId,
				auditWorkflowName: wfName || undefined,
			};
		}
		return {
			message: `Workflow is still running after ${activation.maxWaitApplied ?? 300} seconds. Trace ID: ${activation.traceId}. Use Audit Reporting to monitor progress.`,
			role: "success",
			auditTraceId: activation.traceId,
			auditWorkflowName: wfName || undefined,
		};
	}

	return { message: "Unexpected activation response.", role: "danger" };
}
